import "../src/css/App.css";
import { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";

import { auth, db } from "./backend/firebaseConfig";
import { useUserRole } from "./context/UserRoleContext";
import { useUserEmail } from "./context/UserEmailContext";
import { useAdminMails } from "./context/AdminEmailsContext";
import { normalizeEmailList, normalizeRole } from "./utils/amlAuthUtils";

import SignIn from "./frontend/SignIn/SignIn";
import AMLDashboard from "./frontend/Dashboard/AMLDashboard";
import NewCheck from "./frontend/Checks/NewCheck";
import Guide from "./frontend/Pages/Guide";
import CheckResult from "./frontend/CheckDetails/CheckDetails";
import Statement from "./frontend/Statement/Statement";

function ProtectedRoute({ children }) {
  const [authChecked, setAuthChecked] = useState(false);
  const [authed, setAuthed] = useState(false);

  const { setUserRole } = useUserRole();
  const { setUserEmail } = useUserEmail();
  const { setAdminMails } = useAdminMails();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      const role = localStorage.getItem("check2UserRole");
      const email = localStorage.getItem("check2UserEmail");

      if (firebaseUser && role && email) {
        setAuthed(true);
      } else {
        // Firebase session gone or local auth state missing — force full sign-out
        try {
          await signOut(auth);
        } catch {}
        setUserRole(null);
        setUserEmail(null);
        setAdminMails([]);
        localStorage.clear();
        sessionStorage.clear();
        setAuthed(false);
      }

      setAuthChecked(true);
    });

    return () => unsubscribe();
  }, [setUserRole, setUserEmail, setAdminMails]);

  if (!authChecked) return null;
  if (!authed) return <Navigate to="/" replace />;
  return children;
}

function App() {
  const { userEmail } = useUserEmail();
  const { setUserRole } = useUserRole();
  const { setAdminMails, setAllowedCheckTypes } = useAdminMails();

  // ── Live listener on the signed-in user's own Firestore document ──────────
  // Runs on every mount (incl. page refresh) so adminMails / role /
  // allowedCheckTypes are always in sync — no sign-out required.
  useEffect(() => {
    if (!userEmail) return;

    const userDocRef = doc(db, "AML Checks", userEmail);

    const unsub = onSnapshot(
      userDocRef,
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();

        // Deactivated account — force sign-out immediately
        if (data.active === false) {
          signOut(auth).catch(() => {});
          setUserRole(null);
          setAdminMails([]);
          setAllowedCheckTypes([]);
          localStorage.clear();
          sessionStorage.clear();
          return;
        }

        // Sync role (persisted to localStorage so ProtectedRoute stays valid)
        const newRole = normalizeRole(data.role);
        setUserRole(newRole);
        localStorage.setItem("check2UserRole", newRole);

        // Sync managed users — critical for admin seeing their users' checks
        setAdminMails(normalizeEmailList(data.users || []));

        // Sync allowed check types
        setAllowedCheckTypes(
          Array.isArray(data.allowedCheckTypes) ? data.allowedCheckTypes : [],
        );
      },
      (error) => {
        console.error("[App] Error listening to user document:", error);
      },
    );

    return () => unsub();
  }, [userEmail, setUserRole, setAdminMails, setAllowedCheckTypes]);

  return (
    <Router>
      <Routes>
        <Route path="/" element={<SignIn />} />
        {/* <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <AMLDashboard />
            </ProtectedRoute>
          }
        /> */}
        <Route
          path="/my-checks"
          element={
            <ProtectedRoute>
              <AMLDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/new-check"
          element={
            <ProtectedRoute>
              <NewCheck />
            </ProtectedRoute>
          }
        />
        <Route
          path="/check²/result"
          element={
            <ProtectedRoute>
              <CheckResult />
            </ProtectedRoute>
          }
        />
        <Route
          path="/guide"
          element={
            <ProtectedRoute>
              <Guide />
            </ProtectedRoute>
          }
        />
        <Route
          path="/statement"
          element={
            <ProtectedRoute>
              <Statement />
            </ProtectedRoute>
          }
        />
        <Route path="/*" element={<SignIn />} />
      </Routes>
    </Router>
  );
}

export default App;
